import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Check, X, ArrowRight } from "lucide-react";
import { handleCheckout } from "../../utils/checkout";
import { useExchangeRate, toKRWLabel } from "../../hooks/useExchangeRate";
import { SERVICE_PRICES, diagnosisPrice } from "../../constant";
import { analytics, EVENT_FN } from "../../analytics";
import { DIGITAL_PRICES } from "../../constant";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay } }),
};

const RESOURCE_HIGHLIGHTS = [
  { label: "UK CV 템플릿", desc: "ATS 통과율 높이는 영국 표준 포맷", price: `£${DIGITAL_PRICES["resume-kit"]}` },
  { label: "인터뷰 가이드", desc: "영국식 STAR 기법 30+ Q&A 포함", price: `£${DIGITAL_PRICES["interview-guide-student"]}~` },
  { label: "LinkedIn 가이드", desc: "리크루터에게 발견되는 프로필 전략", price: `£${DIGITAL_PRICES["linkedin-guide"]}` },
];

const CheckService: React.FC = () => {
  const { t } = useTranslation();
  const { rate } = useExchangeRate();
  const navigate = useNavigate();

  const section = t("checkService", { returnObjects: true }) as {
    heading: { line1: string; line2: string };
    description: { line1: string; highlight: string; line2: string; line3: string; line4: string };
    product: { title: string; features: string[] };
    notFor: { title: string; items: string[] };
    cta: { button: string; note: string };
    processGuide: { content: string };
  };

  return (
    <>
      <Helmet>
        <title>영국 CV 현실 점검 — UK CV Reality Check | BeMe Consulting</title>
        <meta
          name="description"
          content="영국 리크루터 기준으로 내 CV가 어떻게 보이는지 점검해보세요. 탈락 원인 Top 5 분석, Before/After 예시, 개선 방향 제시. BeMe Consulting의 UK CV Reality Check 서비스."
        />
        <meta
          name="keywords"
          content="영국 CV 점검, 영국 이력서 진단, UK CV review, 영국 취업 준비, CV 피드백, 영국 리크루터"
        />
        <link rel="canonical" href="https://www.bemeconsulting.com/#/check-service" />
        <meta property="og:title" content="영국 CV 현실 점검 — BeMe Consulting" />
        <meta
          property="og:description"
          content="영국 리크루터 기준으로 내 CV가 어떻게 보이는지 확인하세요. 탈락 가능성 높은 핵심 문제 Top 5 + 개선 방향."
        />
      </Helmet>

      {/* ── Hero ── */}
      <section className="bg-[#0A1A3E] text-white pt-32 pb-20 px-6 text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={0}
          className="max-w-2xl mx-auto"
        >
          <span className="inline-block text-xs font-bold tracking-widest text-gold uppercase mb-4">
            Diagnosis Service
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-5">
            {section.heading.line1}
            <br />
            <span className="text-gold">{section.heading.line2}</span>
          </h1>
          <p className="text-white/70 text-base md:text-lg leading-relaxed">
            {section.description.line1}{" "}
            <strong className="text-white">{section.description.highlight}</strong>
            {section.description.line2}
            <br />
            {section.description.line3}
            <br className="hidden md:block" />
            {section.description.line4}
          </p>
        </motion.div>
      </section>

      {/* ── Product Card ── */}
      <section className="bg-[#F5F6F7] py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={0}
            className="bg-white rounded-2xl shadow-sm overflow-hidden"
          >
            {/* Card header */}
            <div className="bg-[#0A1A3E] px-8 py-5 flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-white font-extrabold text-lg">UK CV Reality Check</h2>
              <div className="flex items-baseline gap-2">
                <span className="text-gold text-3xl font-extrabold">£{SERVICE_PRICES.diagnosis}</span>
                {rate && (
                  <span className="text-white/50 text-sm">
                    · {toKRWLabel(SERVICE_PRICES.diagnosis * rate)} 상당
                  </span>
                )}
              </div>
            </div>

            <div className="p-8 md:p-10 grid md:grid-cols-2 gap-8">
              {/* Features */}
              <div>
                <p className="text-xs font-bold tracking-widest text-[#d4a017] uppercase mb-4">포함 내용</p>
                <ul className="space-y-3">
                  {Array.isArray(section.product.features) &&
                    section.product.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                        <Check size={15} className="text-[#0A1A3E] mt-0.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                </ul>
              </div>

              {/* Not for */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-5">
                <p className="text-xs font-bold tracking-widest text-gray-400 uppercase mb-4">
                  이런 분께는 맞지 않습니다
                </p>
                <ul className="space-y-3">
                  {section.notFor.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-500">
                      <X size={14} className="text-red-400 mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* CTA */}
            <div className="border-t border-gray-100 px-8 md:px-10 py-8 text-center bg-gray-50">
              <motion.button
                onClick={() => {
                  analytics.track(EVENT_FN.paymentLink("diagnosis", "checkService"));
                  handleCheckout("diagnosis");
                }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="bg-[#0A1A3E] text-white font-extrabold px-10 py-4 rounded-full text-base hover:bg-[#305485] transition-colors shadow-md"
              >
                {section.cta.button.replace("{Check_Price}", diagnosisPrice.toString())}
              </motion.button>

              <p className="mt-3 text-xs text-gray-400 whitespace-pre-line">{section.cta.note}</p>

              <div
                className="mt-4 text-xs text-gray-500 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: section.processGuide.content }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Resources CTA ── */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={0}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0A1A3E] mb-3">
              아직 준비가 안 됐다면?
            </h2>
            <p className="text-gray-500 text-sm md:text-base leading-relaxed">
              진단 전에 방향을 먼저 잡고 싶다면, 전문가가 만든 자료로 시작해보세요.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {RESOURCE_HIGHLIGHTS.map((r, i) => (
              <motion.div
                key={r.label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                custom={i * 0.08}
                className="border border-gray-100 rounded-2xl p-6 hover:shadow-md transition-shadow"
              >
                <p className="text-[#d4a017] text-xs font-bold tracking-widest uppercase mb-2">Digital Resource</p>
                <h3 className="text-[#0A1A3E] font-extrabold text-base mb-1">{r.label}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{r.desc}</p>
                <span className="text-[#0A1A3E] font-bold text-lg">{r.price}</span>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate("/resources")}
              className="inline-flex items-center gap-2 bg-[#0A1A3E] text-white font-bold px-8 py-3.5 rounded-full text-sm hover:bg-[#305485] transition-colors"
            >
              자료 페이지 전체 보기 <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default CheckService;
